import React from "react";
import {
  Box,
  Dialog,
  Divider,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { Grow } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Button, ValidatorForm } from "components";

// Custom Transition Component
const Transition = React.forwardRef(function Transition(props, ref) {
  return (
    <Grow
      ref={ref}
      {...props}
      style={{
        transformOrigin: "center center",
        transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
      }}
    />
  );
});

/**
 * DataSetupDrawer - Reusable modal dialog component for data setup forms
 * Includes styled header with card icon, title, and subtitle
 * @param {boolean} open - Modal open state
 * @param {function} onClose - Close handler
 * @param {string} iconSrc - Icon image path or MUI icon component
 * @param {string} title - Modal title (e.g., "Add Category")
 * @param {string} subtitle - Modal subtitle (e.g., "Create a new category")
 * @param {ReactNode} children - Form content
 * @param {function} onSubmit - Submit handler
 * @param {string} submitLabel - Submit button label (default: "Submit")
 * @param {boolean} loading - Loading state for submit button
 */
const DataSetupDrawer = ({
  open = false,
  onClose,
  iconSrc,
  title,
  subtitle,
  children,
  onSubmit,
  submitLabel = "Submit",
  loading = false,
  maxWidth = "sm",
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      TransitionComponent={Transition}
      transitionDuration={{
        enter: 400,
        exit: 300,
      }}
      PaperProps={{
        sx: {
          display: "flex",
          flexDirection: "column",
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          maxHeight: "90vh",
          boxShadow: `0 20px 60px rgba(0, 0, 0, 0.15)`,
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
          transition: "all 0.4s ease-in-out",
        },
      }}
    >
      <ValidatorForm
        onSubmit={onSubmit}
        sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
      >
        {/* ===== Header with Icon ===== */}
        <Box
          sx={{
            p: 3,
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* Icon Container */}
          {typeof iconSrc === "string" ? (
            <Box
              component="img"
              src={iconSrc}
              alt="Icon"
              sx={{
                width: 48,
                height: 48,
                minWidth: 48,
                objectFit: "contain",
                filter:
                  "brightness(0) saturate(100%) invert(43%) sepia(99%) saturate(1235%) hue-rotate(244deg)",
                opacity: 0.8,
              }}
            />
          ) : (
            <Box
              sx={{
                width: 48,
                height: 48,
                minWidth: 48,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.75rem",
                color: theme.palette.primary.main,
              }}
            >
              {iconSrc}
            </Box>
          )}

          {/* Title and Subtitle */}
          <Box sx={{ flexGrow: 1, pr: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 0.5,
                letterSpacing: 0.2,
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.875rem",
              }}
            >
              {subtitle}
            </Typography>
          </Box>

          {/* Close Button */}
          <IconButton
            onClick={onClose}
            sx={{
              mt: -0.5,
              color: theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: `${theme.palette.primary.main}20`,
                color: theme.palette.primary.main,
              },
              transition: "all 0.2s ease",
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* ===== Content ===== */}
        <Box
          sx={{
            p: 3,
            flexGrow: 1,
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: 6,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.background.default,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.divider,
              borderRadius: 3,
              "&:hover": {
                backgroundColor: theme.palette.text.disabled,
              },
            },
          }}
        >
          <Stack spacing={1}>{children}</Stack>
        </Box>

        <Divider />

        {/* ===== Footer ===== */}
        <Box
          sx={{
            p: 3,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            backgroundColor: theme.palette.background.default,
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={loading}
            sx={{
              flex: 1,
              textTransform: "none",
              fontWeight: 600,
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
              py: 1.2,
              "&:hover": {
                borderColor: theme.palette.primary.main,
                backgroundColor: `${theme.palette.primary.main}10`,
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            type="submit"
            loading={loading}
            sx={{
              flex: 1,
              textTransform: "none",
              fontWeight: 700,
              py: 1.2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              color: "#ffffff",
              fontSize: "1rem",
              letterSpacing: 0.5,
              boxShadow: `0 4px 15px rgba(102, 126, 234, 0.3)`,
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: `0 8px 25px rgba(102, 126, 234, 0.4)`,
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
              },
              "&:active": {
                transform: "translateY(0px)",
              },
            }}
          >
            {submitLabel}
          </Button>
        </Box>
      </ValidatorForm>
    </Dialog>
  );
};

export default DataSetupDrawer;
